import { Badge } from '@mantine/core';
import { IconLink, IconLock } from '@tabler/icons-react';

/**
 * Visual indicator for a content item's visibility — used by course cards,
 * KB cards and detail pages. Distinguishes the three visibility types:
 *
 *  - <c>Public</c>: no badge (it's the default; clutter to display).
 *  - <c>Unlisted</c>: link icon + amber label (anyone with the link).
 *  - <c>Restricted</c> (or any other non-Public name): lock icon + colour
 *    from the lookup row.
 *
 * Hidden entirely when <c>visibilityName</c> is missing or "Public".
 */
export interface VisibilityBadgeProps {
  visibilityName?: string | null;
  visibilityColor?: string | null;
  size?: 'xs' | 'sm' | 'md';
}

const PUBLIC = 'public';
const UNLISTED = 'unlisted';

export function VisibilityBadge({
  visibilityName,
  visibilityColor,
  size = 'sm',
}: VisibilityBadgeProps) {
  const name = (visibilityName ?? '').trim();
  if (!name) return null;
  const lower = name.toLowerCase();
  if (lower === PUBLIC) return null;

  if (lower === UNLISTED) {
    return (
      <Badge
        variant="light"
        color={visibilityColor ?? 'orange'}
        size={size}
        leftSection={<IconLink size={10} />}
      >
        {name}
      </Badge>
    );
  }

  // Restricted (or any legacy/role-gated name) — lock affordance.
  return (
    <Badge
      variant="light"
      color={visibilityColor ?? 'grape'}
      size={size}
      leftSection={<IconLock size={10} />}
    >
      {name}
    </Badge>
  );
}
